import { useEffect, useRef, useState } from "react";
import { PORTRAIT_CANVAS, PORTRAIT_INKS, type PortraitStroke } from "@unfaithful/shared";

const { width: W, height: H, maxPoints } = PORTRAIT_CANVAS;

/** Keep a stroke within the point limit by dropping every other point until it fits. */
function thin(points: number[]): number[] {
  let pts = points;
  while (pts.length / 2 > maxPoints) pts = pts.filter((_, i) => Math.floor(i / 2) % 2 === 0);
  return pts;
}

function paint(ctx: CanvasRenderingContext2D, strokes: PortraitStroke[], live: { points: number[]; ink: number } | null) {
  ctx.clearRect(0, 0, W, H);
  for (const s of live ? [...strokes, live] : strokes) {
    if (s.points.length < 2) continue;
    ctx.strokeStyle = PORTRAIT_INKS[s.ink] ?? PORTRAIT_INKS[0];
    ctx.lineWidth = 9;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(s.points[0]!, s.points[1]!);
    for (let i = 2; i + 1 < s.points.length; i += 2) ctx.lineTo(s.points[i]!, s.points[i + 1]!);
    if (s.points.length === 2) ctx.lineTo(s.points[0]! + 0.1, s.points[1]!);
    ctx.stroke();
  }
}

/**
 * The artist's pad. Each finished stroke is sent to the TV; the pad redraws
 * from what the castle has, so the phone and the TV always show the same picture.
 */
export function DrawingPad({
  strokes,
  onStroke,
  onWipe,
  enabled,
}: {
  strokes: PortraitStroke[];
  onStroke?: (s: PortraitStroke) => void;
  onWipe?: () => void;
  enabled: boolean;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const live = useRef<{ points: number[]; ink: number } | null>(null);
  const [ink, setInk] = useState(0);

  useEffect(() => {
    const ctx = canvas.current?.getContext("2d");
    if (ctx) paint(ctx, strokes, live.current);
  }, [strokes]);

  const at = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return [Math.round(((e.clientX - r.left) / r.width) * W), Math.round(((e.clientY - r.top) / r.height) * H)].map((v, i) =>
      Math.max(0, Math.min(i === 0 ? W : H, v)),
    ) as [number, number];
  };

  return (
    <div className={`pad ${enabled ? "" : "is-view"}`}>
      <canvas
        ref={canvas}
        width={W}
        height={H}
        className="pad-canvas"
        onPointerDown={(e) => {
          if (!enabled) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          live.current = { points: at(e), ink };
        }}
        onPointerMove={(e) => {
          if (!live.current) return;
          const [x, y] = at(e);
          const p = live.current.points;
          // Skip tiny movements; they only add weight.
          if (Math.hypot(x - p[p.length - 2]!, y - p[p.length - 1]!) < 6) return;
          p.push(x, y);
          const ctx = canvas.current?.getContext("2d");
          if (ctx) paint(ctx, strokes, live.current);
        }}
        onPointerUp={() => {
          const s = live.current;
          live.current = null;
          if (s && onStroke) onStroke({ points: thin(s.points), ink: s.ink });
        }}
        onPointerCancel={() => (live.current = null)}
      />
      {enabled && (
        <div className="pad-tools">
          {PORTRAIT_INKS.map((c, i) => (
            <button
              key={c}
              className={`pad-ink ${ink === i ? "is-on" : ""}`}
              style={{ background: c }}
              aria-label={`Ink ${i + 1}`}
              aria-pressed={ink === i}
              onClick={() => setInk(i)}
            />
          ))}
          <button className="text-button pad-wipe" onClick={onWipe}>
            Wipe
          </button>
        </div>
      )}
    </div>
  );
}
